import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { EASE_OUT } from "lib/ease";
import { cn } from "lib/utils";
import "./tooltip.css";

const GAP = 8;
const WARM_WINDOW_MS = 300;

const anchorTransform = {
  top: "translate(-50%, -100%)",
  bottom: "translate(-50%, 0)",
  left: "translate(-100%, -50%)",
  right: "translate(0, -50%)",
};

const transformOrigin = {
  top: "center bottom",
  bottom: "center top",
  left: "right center",
  right: "left center",
};

const offsetFrom = {
  top: { y: 8 },
  bottom: { y: -8 },
  left: { x: 8 },
  right: { x: -8 },
};

let lastHiddenAt = 0;

function getMotionVariants(side, reduce) {
  if (reduce) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: 0.14, ease: EASE_OUT } },
      exit: { opacity: 0, transition: { duration: 0.1, ease: EASE_OUT } },
    };
  }

  const offset = offsetFrom[side];

  return {
    initial: {
      opacity: 0,
      scale: 0.9,
      filter: "blur(5px)",
      x: offset.x || 0,
      y: offset.y || 0,
    },
    animate: {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 380,
        damping: 30,
        mass: 0.7,
        opacity: { duration: 0.14, ease: EASE_OUT },
        filter: { duration: 0.18, ease: EASE_OUT },
      },
    },
    exit: {
      opacity: 0,
      scale: 0.94,
      filter: "blur(3px)",
      x: (offset.x || 0) * 0.6,
      y: (offset.y || 0) * 0.6,
      transition: { duration: 0.12, ease: EASE_OUT },
    },
  };
}

function BeUITooltip({
  content,
  children,
  side = "top",
  delay = 120,
  className,
  wrapperClassName,
  anchorRef: externalAnchorRef,
  anchorPoint,
  open: controlledOpen,
  onOpenChange,
  id: providedId,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (nextOpen) => {
      if (controlledOpen === undefined) setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange],
  );
  const [coords, setCoords] = useState(null);
  const generatedId = useId();
  const tooltipId = providedId || `tooltip-${generatedId.replace(/:/g, "")}`;
  const timer = useRef(null);
  const wrapperRef = useRef(null);
  const surfaceRef = useRef(null);
  const tapRef = useRef(null);
  const anchor = externalAnchorRef || wrapperRef;
  const reduce = useReducedMotion();
  const variants = useMemo(() => getMotionVariants(side, reduce), [reduce, side]);

  const place = useCallback(() => {
    const element = anchor.current;
    if (!element || typeof window === "undefined") return;

    const rect = element.getBoundingClientRect();
    const pointX = rect.left + rect.width * (anchorPoint?.x ?? 0.5);
    const pointY = rect.top + rect.height * (anchorPoint?.y ?? 0.5);
    const points = {
      top: { top: (anchorPoint ? pointY : rect.top) - GAP, left: pointX },
      bottom: { top: (anchorPoint ? pointY : rect.bottom) + GAP, left: pointX },
      left: { top: pointY, left: (anchorPoint ? pointX : rect.left) - GAP },
      right: { top: pointY, left: (anchorPoint ? pointX : rect.right) + GAP },
    };
    const next = { ...points[side] };
    const width = surfaceRef.current?.offsetWidth || 0;
    const height = surfaceRef.current?.offsetHeight || 0;
    const dx = side === "left" ? width : side === "right" ? 0 : width / 2;
    const dy = side === "top" ? height : side === "bottom" ? 0 : height / 2;

    next.left = Math.max(GAP + dx, Math.min(next.left, window.innerWidth - GAP - width + dx));
    next.top = Math.max(GAP + dy, Math.min(next.top, window.innerHeight - GAP - height + dy));
    setCoords((previous) => (
      previous?.top === next.top && previous?.left === next.left ? previous : next
    ));
  }, [anchor, anchorPoint, side]);

  const show = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    const warm = Date.now() - lastHiddenAt < WARM_WINDOW_MS;
    timer.current = window.setTimeout(() => {
      place();
      setOpen(true);
      timer.current = null;
    }, warm ? 0 : delay);
  }, [delay, place, setOpen]);

  const hide = useCallback(() => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (open) lastHiddenAt = Date.now();
    setOpen(false);
  }, [open, setOpen]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    place();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(place);
    if (anchor.current) observer.observe(anchor.current);
    if (coords && surfaceRef.current) observer.observe(surfaceRef.current);
    return () => observer.disconnect();
  }, [anchor, coords, open, place]);

  useEffect(() => {
    if (!open) return undefined;

    const handleMove = () => place();
    const handleDismiss = (event) => {
      const target = event.target;
      if (anchor.current?.contains(target) || surfaceRef.current?.contains(target)) return;
      hide();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") hide();
    };

    window.addEventListener("scroll", handleMove, true);
    window.addEventListener("resize", handleMove);
    document.addEventListener("pointerdown", handleDismiss, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("scroll", handleMove, true);
      window.removeEventListener("resize", handleMove);
      document.removeEventListener("pointerdown", handleDismiss, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchor, hide, open, place]);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  if (!externalAnchorRef && !isValidElement(children)) return children;

  const describedBy = children?.props?.["aria-describedby"];
  const trigger = isValidElement(children)
    ? cloneElement(children, {
        "aria-describedby": describedBy && describedBy !== tooltipId
          ? `${describedBy} ${tooltipId}`
          : tooltipId,
      })
    : null;

  return (
    <>
      {!externalAnchorRef ? (
        <span
          ref={wrapperRef}
          className={cn("ui-tooltip-wrapper", wrapperClassName)}
          onPointerEnter={(event) => {
            if (event.pointerType !== "touch") show();
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== "touch") hide();
          }}
          onFocus={show}
          onBlur={hide}
          onPointerDown={(event) => {
            tapRef.current = { pointerType: event.pointerType, wasOpen: open };
          }}
          onPointerCancel={() => {
            tapRef.current = null;
          }}
          onKeyDown={() => {
            tapRef.current = null;
          }}
          onClick={() => {
            const tap = tapRef.current;
            tapRef.current = null;
            if (!tap || tap.pointerType === "mouse") return;
            if (tap.wasOpen) hide();
            else {
              place();
              setOpen(true);
            }
          }}
        >
          {trigger}
        </span>
      ) : null}
      {typeof document !== "undefined"
        ? createPortal(
            <AnimatePresence>
              {open && coords ? (
                <span
                  className="ui-tooltip-positioner"
                  style={{
                    top: coords.top,
                    left: coords.left,
                    transform: anchorTransform[side],
                  }}
                >
                  <motion.span
                    ref={surfaceRef}
                    id={tooltipId}
                    role="tooltip"
                    aria-live="polite"
                    variants={variants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className={cn("ui-tooltip-surface", className)}
                    data-side={side}
                    style={{ transformOrigin: transformOrigin[side] }}
                  >
                    {content}
                  </motion.span>
                </span>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </>
  );
}

// The dashboard uses the beUI-style one-piece Tooltip API. These exports stay
// available for the existing Base UI sidebar composition.
function Tooltip(props) {
  const usesBeUIApi = props.content !== undefined
    || props.anchorRef
    || props.anchorPoint
    || props.open !== undefined
    || props.onOpenChange
    || props.wrapperClassName;

  return usesBeUIApi
    ? <BeUITooltip {...props} />
    : <BaseTooltip.Root data-slot="tooltip" {...props} />;
}

function TooltipProvider({ delay = 0, ...props }) {
  return <BaseTooltip.Provider data-slot="tooltip-provider" delay={delay} {...props} />;
}

function TooltipTrigger(props) {
  return <BaseTooltip.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  children,
  ...props
}) {
  return (
    <BaseTooltip.Portal>
      <BaseTooltip.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <BaseTooltip.Popup
          data-slot="tooltip-content"
          className={cn("ui-tooltip-legacy-content", className)}
          {...props}
        >
          {children}
          <BaseTooltip.Arrow className="ui-tooltip-legacy-arrow" />
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
